<template>
  <div class="appCont">
    <!-- Global upload progress bar -->
    <MicroProgressBar :visible="hasActiveUploads" :progress="uploadProgress" />

    <!-- Global auth gate loader -->
    <LoadingState v-if="!authReady" variant="page" message="Loading Unit…" />

    <PasswordPage
      v-if="showPasswordPage"
      @passwordEntered="handlePasswordEntered"
    />
    <!-- <DraggableBackButton /> -->

    <Navbar v-if="authReady" />
    <router-view v-if="authReady" v-slot="{ Component, route }">
      <keep-alive :include="['Dashboard', 'EcosystemView']">
        <component
          :is="Component"
          :key="route.meta.usePathKey ? route.path : undefined"
          :class="{ 'direct-auth-route': route.meta.directAuthLayout }"
        />
      </keep-alive>
    </router-view>
    <Modal
      v-if="showLoginModal"
      v-model:visible="showLoginModal"
      @close="toggleLoginModal"
    >
      <template v-slot:header>
        <div class="modal-switch" @click="switchToRegister" v-haptic>
          <svg class="tinyImg"><use href="@/assets/icons/iconset.svg#arrowleft_tiny"></use></svg>
          Register
        </div>
      </template>
      <template v-slot:content>
        <Login :redirect="true" />
      </template>
    </Modal>
    <Modal
      v-if="showRegisterModal"
      v-model:visible="showRegisterModal"
      @close="toggleRegisterModal"
    >
      <template v-slot:header>
        <div class="modal-switch" @click="switchToLogin" v-haptic>
          <svg class="tinyImg"><use href="@/assets/icons/iconset.svg#arrowleft_tiny"></use></svg>
          Login
        </div>
      </template>
      <template v-slot:content>
        <Register :redirect="true" />
      </template>
    </Modal>
    <Messages
      v-if="user.loggedIn && areMessagesOpen"
      :isOpen="areMessagesOpen"
      :currentUser="userCredentials"
      otherUserUid="123"
    />

    <!-- Global item overlay (fallback when no local component owns the overlay) -->
    <ImageSlideshowOverlay
      v-if="globalOverlayVisible"
      :visible="globalOverlayVisible"
      :item="globalOverlayItem"
      @close="closeGlobalOverlay"
      @edit="handleGlobalOverlayEdit"
    />

    <!-- Global floating media player -->
    <FloatingPlayer v-if="floatingPlayerActive" />
  </div>
</template>

<script>
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { computed, defineAsyncComponent, onUnmounted, ref, watch } from "vue";
import { auth } from "@/firebase";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

import PasswordPage from "@/components/GenericComponents/PasswordPage.vue";
import Navbar from "@/components/NavbarComponents/Navbar.vue";
import LoadingState from "@/components/GenericComponents/LoadingState.vue";
import MicroProgressBar from "@/components/GenericComponents/MicroProgressBar.vue";
import { fetchItemById } from "@/composables/useItemFetch";
import {
  beginBootRun,
  beginBootStep,
  installBootLifecycleDiagnostics,
} from "@/utils/bootDiagnostics";
import { closeItemOverlayRoute } from "@/utils/overlayNavigation";
import { appearanceRuntime } from "@/services/appearanceRuntime";
import { isAccountAdministrationFeatureEnabled } from "@/config/runtimeConfig";
// import DraggableBackButton from "@/components/NavbarComponents/DraggableBackButton.vue";

const Login = defineAsyncComponent(() =>
  import("@/components/AuthComponents/Login.vue")
);
const Register = defineAsyncComponent(() =>
  import("@/components/AuthComponents/Register.vue")
);
const Modal = defineAsyncComponent(() =>
  import("@/components/AuthComponents/Modal.vue")
);
const Messages = defineAsyncComponent(() =>
  import("@/components/MessagingComponents/PrivateMessages.vue")
);
const ImageSlideshowOverlay = defineAsyncComponent(() =>
  import("@/components/GenericComponents/ImageSlideshowOverlay.vue")
);
const FloatingPlayer = defineAsyncComponent(() =>
  import("@/components/GenericComponents/FloatingPlayer.vue")
);

export default {
  name: "App",
  components: {
    PasswordPage,
    Navbar,
    Register,
    Login,
    Modal,
    Messages,
    LoadingState,
    MicroProgressBar,
    ImageSlideshowOverlay,
    FloatingPlayer,
    // DraggableBackButton,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const assetPath = ref(`${window.location.origin}/assets`);
    const authReady = ref(false);
    let globalOverlayGeneration = 0;
    installBootLifecycleDiagnostics();
    beginBootRun({ route: String(route.name || "unknown") });
    const stopNavigationDiagnosticsWatch = watch(
      () => route.fullPath,
      () => beginBootRun({ route: String(route.name || "unknown") }),
    );

    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      globalOverlayGeneration += 1;
      const finishAuth = beginBootStep("auth", { signedIn: !!user });
      if (user) {
        await store.dispatch("logInStateCommit", { user });
        // Authentication owns the shell gate. Membership hydration is
        // progressive and must not leave returning users on a permanent loader.
        authReady.value = true;
        finishAuth({ outcome: "ready" });

        // Own profile links need the account's canonical public identifier.
        // The store action is generation-guarded and never gates the shell.
        Promise.resolve(
          store.dispatch("ensureOwnPublicIdentifier", { userUID: user.uid })
        ).catch(() => {});

        // Announce the outcome of account deletions this administrator
        // started before closing the window.
        if (isAccountAdministrationFeatureEnabled) {
          import("@/services/accountDeletionProgressTracker")
            .then(({ accountDeletionProgressTracker }) =>
              accountDeletionProgressTracker.resume(user.uid)
            )
            .catch(() => {});
        }

        // Resolve only the caller-owned membership summary and active org here.
        // Expanded org/member/role data belongs to the screens that need it.
        try {
          await store.dispatch("ensureSelectedOrg", { userUID: user.uid });
        } catch (_error) {
          // The active screen owns the recoverable membership error state.
        }
      } else {
        await store.dispatch("logOutStateCommit");
        authReady.value = true;
        finishAuth({ outcome: "signed-out" });
      }
    });

    onUnmounted(() => unsubscribeAuth());

    const user = computed(() => store.getters.user);
    const userCredentials = computed(() => store.getters.userCredentials);
    const selectedOrgUID = computed(() => store.getters.selectedOrgUID);
    const stopWalletContextWatch = watch(
      () => [userCredentials.value?.data?.uid || null, selectedOrgUID.value],
      ([uid, orgUID]) => {
        if (!uid || !orgUID) {
          Promise.resolve(store.dispatch("stopActiveWalletSubscription")).catch(
            () => {}
          );
          store.commit("CLEAR_USER_ROLE_PRIVILEGES");
          return;
        }
        Promise.resolve(
          store.dispatch("startActiveWalletSubscription", { uid, orgUID })
        ).catch(() => {});
        Promise.resolve(
          store.dispatch("getUserRolePrivileges", { orgUID })
        ).catch(() => {});
      },
      { immediate: true },
    );
    const stopAppearanceContextWatch = watch(
      () => [userCredentials.value?.data?.uid || null, selectedOrgUID.value],
      async ([uid, orgId], previous = []) => {
        const previousUid = previous?.[0] || null;
        try {
          if (!uid || !orgId) {
            await store.dispatch("appearance/clearForAuthChange", { previousUid });
            return;
          }
          await store.dispatch("appearance/loadForOrganisation", { uid, orgId });
        } catch (_error) {
          // Appearance is optional and must not reject the authenticated shell.
        }
      },
      { immediate: true },
    );
    const stopDesignSystemAppearanceWatch = watch(
      () => route.name,
      (routeName) => {
        appearanceRuntime.forceUnitReference(routeName === "design-system");
      },
      { immediate: true },
    );
    onUnmounted(() => {
      stopWalletContextWatch();
      stopAppearanceContextWatch();
      stopDesignSystemAppearanceWatch();
      stopNavigationDiagnosticsWatch();
      Promise.resolve(store.dispatch("stopActiveWalletSubscription")).catch(
        () => {}
      );
    });
    const areMessagesOpen = computed(
      () => store.getters["UIState/areMessagesOpen"]
    );

    const showPasswordPage = ref(false);

    const currentRoute = router.currentRoute;
    const isInviteRoute = computed(() => currentRoute.value.name === "invite");

    const handlePasswordEntered = (password) => {
      if (password === "UnitJam") {
        showPasswordPage.value = false;
        router.push("/");
        const successMessage = t("app.welcome");
        toast.success(successMessage, { autoClose: 2000 });
      } else {
        const errorMessage = t("app.incorrectPassword");
        toast.error(errorMessage, { autoClose: 2000 });
      }
    };

    const showLoginModal = computed(() => store.state.showLoginModal);
    const showRegisterModal = computed(() => store.state.showRegisterModal);

    const toggleLoginModal = () => store.dispatch("toggleLoginModal");
    const toggleRegisterModal = () => store.dispatch("toggleRegisterModal");

    const switchToRegister = () => {
      if (showLoginModal.value) toggleLoginModal();
      toggleRegisterModal();
    };
    const switchToLogin = () => {
      if (showRegisterModal.value) toggleRegisterModal();
      toggleLoginModal();
    };

    // onMounted(() => {
    //   store.dispatch("UIState/initDarkMode");
    // });

    // Upload progress bar state
    const hasActiveUploads = computed(
      () => store.getters["uploadQueue/hasActiveUploads"]
    );
    const uploadProgress = computed(
      () => store.getters["uploadQueue/overallProgress"]
    );

    // ── Global overlay (fallback for pages without local overlay) ──
    const globalOverlayItem = computed(
      () => store.getters["UIState/globalOverlayItem"]
    );
    const globalOverlayVisible = computed(() => !!globalOverlayItem.value);
    const floatingPlayerActive = computed(
      () => !!store.state.UIState.floatingPlayerItem,
    );

    const closeGlobalOverlay = (closeData) => {
      const closingItem = globalOverlayItem.value;
      const isEmbed = closingItem && (closingItem.mediaType === "embed" || closingItem.embedHtml);
      const isAudio = closingItem && closingItem.mediaType === "audio" && !!closingItem.audioUrl;
      const wasPlaying =
        closeData?.mediaIsPlaying ??
        closeData?.embedIsPlaying ??
        closeData?.audioIsPlaying ??
        false;
      const currentTime =
        closeData?.mediaCurrentTime ??
        (isAudio ? closeData?.audioCurrentTime : closeData?.embedCurrentTime) ??
        0;
      console.log("[FloatingPlayer:App] closeGlobalOverlay", {
        isEmbed,
        isAudio,
        wasPlaying,
        currentTime: Math.floor(currentTime),
        itemId: closingItem?.id,
      });

      if ((isEmbed || isAudio) && wasPlaying) {
        store.dispatch("UIState/playInFloatingPlayer", {
          item: closingItem,
          startTime: Math.floor(currentTime),
        });
        console.log("[FloatingPlayer:App] dispatched playInFloatingPlayer with startTime:", Math.floor(currentTime));
      }

      store.dispatch("UIState/clearGlobalOverlayItem");
      closeItemOverlayRoute(router, route);
      console.log("[FloatingPlayer:App] overlay closed");
    };

    const handleGlobalOverlayEdit = (item) => {
      // Edit action from overlay - emit to parent or handle inline
      // Keep overlay open and let the edit flow handle it
      console.log("[App] Edit requested for item:", item?.id);
      // Note: The edit flow is handled by MasonryMarketplace or ImageSlideshowOverlay
      // This is a fallback for global overlay context
      closeGlobalOverlay();
    };

    // Watch ?item= query param — only act when no local overlay is active
    watch(
      () => [
        route.query.item || null,
        route.query.itemOrg || null,
        userCredentials.value?.data?.uid || null,
        selectedOrgUID.value || null,
      ],
      async ([itemId, itemOrgId, requestedUid, requestedOrgUID]) => {
        const generation = ++globalOverlayGeneration;
        if (!itemId) {
          // URL param removed (e.g. browser back) — close if global overlay is showing
          if (globalOverlayItem.value) {
            store.dispatch("UIState/clearGlobalOverlayItem");
          }
          return;
        }
        // If a local component (MasonryMarketplace) already owns the overlay, skip
        if (store.state.UIState.localOverlayActive) {
          return;
        }

        if (globalOverlayItem.value) {
          store.dispatch("UIState/clearGlobalOverlayItem");
        }

        store.dispatch("UIState/setGlobalOverlayLoading", true);
        try {
          console.log('[App] Fetching exact item placement:', {
            itemId,
            itemOrgId,
          });
          const fetched = await fetchItemById(itemId, itemOrgId);
          console.log('[App] fetchItemById result:', fetched ? { id: fetched.id, type: fetched.type } : null);
          // Re-check all ownership boundaries: a response started under account
          // or organisation A must never be presented after moving to B.
          if (
            generation !== globalOverlayGeneration ||
            requestedUid !== (userCredentials.value?.data?.uid || null) ||
            requestedOrgUID !== (selectedOrgUID.value || null) ||
            route.query.item !== itemId ||
            route.query.itemOrg !== itemOrgId ||
            store.state.UIState.localOverlayActive
          ) {
            return;
          }
          if (fetched) {
            console.log('[App] Setting global overlay item');
            store.dispatch("UIState/setGlobalOverlayItem", fetched);
          } else {
            console.warn(`[App] Item not found for overlay: ${itemId}`);
            router.replace({
              name: "not-found",
              query: { resource: "item" },
            });
          }
        } catch (err) {
          console.error("[App] Error fetching item for overlay:", err);
        } finally {
          if (generation === globalOverlayGeneration) {
            store.dispatch("UIState/setGlobalOverlayLoading", false);
          }
        }
      },
      { immediate: true }
    );

    return {
      assetPath,
      user,
      userCredentials,
      areMessagesOpen,
      authReady,
      showPasswordPage,
      handlePasswordEntered,
      isInviteRoute,
      showLoginModal,
      toggleLoginModal,
      showRegisterModal,
      toggleRegisterModal,
      switchToLogin,
      switchToRegister,
      hasActiveUploads,
      uploadProgress,
      globalOverlayItem,
      globalOverlayVisible,
      floatingPlayerActive,
      closeGlobalOverlay,
      handleGlobalOverlayEdit,
    };
  },
};
</script>

<style src="@/assets/main.css" lang="css"></style>

<style scoped>
.direct-auth-route {
  box-sizing: border-box;
  width: min(
    100%,
    calc(var(--form-content-width-default) + (var(--space-7) * 2))
  );
  margin: 0 auto;
  padding:
    calc(
      var(--control-height-default) + (var(--space-4) * 2) + var(--space-7)
    )
    var(--space-7) var(--space-7);
}

.direct-auth-route :deep(.form-group) {
  min-width: 0;
}

.modal-switch {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-label);
  opacity: 0.35;
  cursor: pointer;
  transition: opacity 0.15s;
  margin-bottom: var(--space-3);
}
.modal-switch:hover {
  opacity: 0.55;
}
</style>
